import { ReservationState } from "@digitalopsdev/serviceops-reservations-components/dist/EditReservation/model";
import { useState, useEffect } from 'react';
import React from 'react';

interface PaymentTypeTermsItem {
    paymentType: string;
    enabled: boolean;
    depositPercent: number;
    fullPaymentFeePercent: number;
    partialPaymentFeePercent: number;
}

// Fees default to 0% for every payment type (operator request) — staff opt INTO a fee per row
// rather than starting from the historical card rates. Mirrors PaymentTypeTerms.GetDefaults().
const DEFAULT_MATRIX: PaymentTypeTermsItem[] = [
    { paymentType: '2C2P', enabled: true, depositPercent: 50, fullPaymentFeePercent: 0, partialPaymentFeePercent: 0 },
    { paymentType: 'Stripe', enabled: true, depositPercent: 50, fullPaymentFeePercent: 0, partialPaymentFeePercent: 0 },
    { paymentType: 'Omise', enabled: false, depositPercent: 50, fullPaymentFeePercent: 0, partialPaymentFeePercent: 0 },
    { paymentType: 'BankTransfer', enabled: false, depositPercent: 50, fullPaymentFeePercent: 0, partialPaymentFeePercent: 0 },
];

interface PaymentTermsEditorComponentProps {
    reservationState: ReservationState;
}

export function PaymentTermsEditorComponent({ reservationState }: PaymentTermsEditorComponentProps) {
    const [matrix, setMatrix] = useState<PaymentTypeTermsItem[]>([]);
    const [status, setStatus] = useState<'loading' | 'ready' | 'saving' | 'saved' | 'error'>('loading');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const reservationNumber = reservationState.ReservationNumber;

    // Load current payment terms from the reservation
    useEffect(() => {
        if (!reservationNumber) {
            setStatus('ready');
            setMatrix(structuredClone(DEFAULT_MATRIX));
            return;
        }

        (async () => {
            try {
                setStatus('loading');
                // Same-origin staff endpoint (mirrors the POST save below). The guest booking-engine
                // confirmation route lives in a separate app on PublicBookingsAppBaseUrl and 404s here.
                const resp = await fetch(`/reservations/${reservationNumber}/payment-terms`);
                if (!resp.ok) {
                    setMatrix(structuredClone(DEFAULT_MATRIX));
                    setStatus('ready');
                    return;
                }
                const data = await resp.json();
                if (Array.isArray(data?.paymentTerms) && data.paymentTerms.length > 0) {
                    setMatrix(data.paymentTerms);
                } else {
                    setMatrix(structuredClone(DEFAULT_MATRIX));
                }
                setStatus('ready');
            } catch {
                setMatrix(structuredClone(DEFAULT_MATRIX));
                setStatus('ready');
            }
        })();
    }, [reservationNumber]);

    const updateItem = (index: number, field: keyof PaymentTypeTermsItem, value: any) => {
        setMatrix(prev => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
        // Reset saved status when user makes changes
        if (status === 'saved') setStatus('ready');
    };

    const handleSave = async () => {
        if (!reservationNumber) {
            setErrorMessage('No reservation number');
            setStatus('error');
            return;
        }

        setStatus('saving');
        setErrorMessage(null);

        try {
            const resp = await fetch(`/reservations/${reservationNumber}/payment-terms`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json; charset=utf-8' },
                body: JSON.stringify({ paymentTerms: matrix }),
            });

            if (!resp.ok) {
                const text = await resp.text();
                throw new Error(text || 'Failed to save payment terms');
            }

            setStatus('saved');
        } catch (err: any) {
            setErrorMessage(err?.message || 'Failed to save');
            setStatus('error');
        }
    };

    const handleReset = () => {
        setMatrix(structuredClone(DEFAULT_MATRIX));
        if (status === 'saved') setStatus('ready');
    };

    if (status === 'loading') {
        return <div style={{ padding: 16 }}>Loading payment terms...</div>;
    }

    const allDisabled = matrix.every(m => !m.enabled);

    return (
        <div style={{ padding: '8px 0' }}>
            <h5 style={{ marginBottom: 12 }}>Payment Terms Matrix</h5>

            {allDisabled && (
                <div style={{ padding: 10, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
                    All payment types are disabled. The confirmation page will be <strong>info-only</strong> (no payment buttons).
                </div>
            )}

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                        <th style={{ padding: '6px 4px' }}>Payment Type</th>
                        <th style={{ padding: '6px 4px', textAlign: 'center' }}>Enabled</th>
                        <th style={{ padding: '6px 4px', textAlign: 'right' }}>Deposit %</th>
                        <th style={{ padding: '6px 4px', textAlign: 'right' }}>Full Fee %</th>
                        <th style={{ padding: '6px 4px', textAlign: 'right' }}>Partial Fee %</th>
                    </tr>
                </thead>
                <tbody>
                    {matrix.map((item, idx) => (
                        <tr key={item.paymentType} style={{ borderBottom: '1px solid #f3f4f6', opacity: item.enabled ? 1 : 0.5 }}>
                            <td style={{ padding: '6px 4px', fontWeight: 500 }}>
                                {item.paymentType === 'BankTransfer' ? 'Bank Transfer' : item.paymentType}
                            </td>
                            <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                                <input
                                    type="checkbox"
                                    checked={item.enabled}
                                    onChange={(e) => updateItem(idx, 'enabled', e.target.checked)}
                                />
                            </td>
                            <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                                <input
                                    type="number"
                                    min={0} max={100} step={1}
                                    value={item.depositPercent}
                                    onChange={(e) => updateItem(idx, 'depositPercent', parseFloat(e.target.value) || 0)}
                                    disabled={!item.enabled}
                                    className="form-control form-control-sm"
                                    style={{ width: 72, textAlign: 'right', display: 'inline-block' }}
                                />
                            </td>
                            <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                                <input
                                    type="number"
                                    min={0} max={100} step={0.01}
                                    value={item.fullPaymentFeePercent}
                                    onChange={(e) => updateItem(idx, 'fullPaymentFeePercent', parseFloat(e.target.value) || 0)}
                                    disabled={!item.enabled}
                                    className="form-control form-control-sm"
                                    style={{ width: 72, textAlign: 'right', display: 'inline-block' }}
                                />
                            </td>
                            <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                                <input
                                    type="number"
                                    min={0} max={100} step={0.01}
                                    value={item.partialPaymentFeePercent}
                                    onChange={(e) => updateItem(idx, 'partialPaymentFeePercent', parseFloat(e.target.value) || 0)}
                                    disabled={!item.enabled}
                                    className="form-control form-control-sm"
                                    style={{ width: 72, textAlign: 'right', display: 'inline-block' }}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {errorMessage && (
                <div style={{ marginTop: 10, padding: 8, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13 }}>
                    {errorMessage}
                </div>
            )}

            {status === 'saved' && (
                <div style={{ marginTop: 10, padding: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, color: '#166534', fontSize: 13 }}>
                    Payment terms saved successfully.
                </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={status === 'saving' || !reservationNumber}
                    style={{ fontSize: 13, padding: '6px 16px' }}
                >
                    {status === 'saving' ? 'Saving...' : 'Save Payment Terms'}
                </button>
                <button
                    className="btn btn-outline-contrast"
                    onClick={handleReset}
                    disabled={status === 'saving'}
                    style={{ fontSize: 13, padding: '6px 16px' }}
                >
                    Reset to Defaults
                </button>
            </div>
        </div>
    );
}
